/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentFactoryResolver, ComponentRef, Injectable, Injector, ViewContainerRef } from '@angular/core';
import { isNotNil, wrapInObservable } from '@gh/core-util';
import { combineLatest$, concat$, of$ } from '@gh/rx';
import { filter$, first$, map$, share$, switchMap$, tap$ } from '@gh/rx/operators';
import { fromPairs, isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { ContextResolve, ContextRoute, ContextRouteAttachment, ContextRouteConfig } from './context-router.interfaces';

/**
 * Internal interface of context route configuration
 */
interface InternalContextRouteConfig {
  injector: Injector;
  route: ContextRouteConfig;
}

/**
 * Internal interface of context route configuration loader.
 */
interface ContextRouteConfigLoader {
  loadRouteConfig(path: string): Observable<InternalContextRouteConfig>;
}

/**
 * This class manages context navigation logic, like loading of route, preloading of data.
 */
export class ContextRouterNavigation {
  config: InternalContextRouteConfig;
  contextRoute: ContextRoute;
  data: Hash<any>;

  promise: Promise<boolean>;

  private resolve: Function;
  private reject: Function;
  private subscription: Subscription;

  constructor(private configLoader: ContextRouteConfigLoader,
              readonly attachment: ContextRouteAttachment,
              public path: string,
              public params: Hash<any>) {
    this.promise = new Promise<boolean>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  start(): void {
    // load route config
    this.subscription = this.configLoader.loadRouteConfig(this.path).pipe(
      tap$((config) => {
        this.config = config;
        this.data = config.route.data || {};
        this.contextRoute = this.buildContextRoute();
      }),
      // resolve route
      switchMap$((config) => {
        const { resolve } = config.route;

        const resolveFields = Object.keys(resolve || {});

        if (resolveFields.length === 0) {
          return [{}];
        } else {
          // tslint:disable-next-line:no-non-null-assertion
          const resolveServices = resolveFields.map((field) => this.config.injector.get(resolve![field]));
          return combineLatest$(resolveServices.map((service) => wrapInObservable(service.resolve(this.contextRoute))))
            .pipe(map$((resolvedData) => fromPairs(resolveFields.map((field, i) => [field, resolvedData[i]]))));
        }
      }),
      // merge data and resolve data
      map$((resolvedData) => ({ ...this.data, ...resolvedData })),
      // update data
      tap$((data) => {
        this.data = data;
        this.contextRoute = this.buildContextRoute();
      }))
      .subscribe(
        () => this.resolve(true),
        (error) => this.reject(error),
      );
  }

  stop(): void {
    const { subscription } = this;
    if (subscription) {
      subscription.unsubscribe();
    }
    this.reject(`Navigation to the route '${this.path}' has been stopped`);
  }

  private buildContextRoute(): ContextRoute {
    return new ContextRoute(this.config.route, this.attachment, this.params, this.data);
  }
}

/**
 * {@link ContextRouterOutlet} manages routing workflow.
 */
export class ContextRouterOutlet {
  private navigation?: ContextRouterNavigation;

  private currentComponentRef: ComponentRef<any>;
  private currentRouteConfig: InternalContextRouteConfig;
  private currentContextRoute: ContextRoute;

  constructor(private configLoader: ContextRouteConfigLoader,
              readonly attachment: ContextRouteAttachment,
              private viewContainerRef?: ViewContainerRef) {

  }

  /**
   * Returns reference to the current instantiated component
   */
  get componentRef(): ComponentRef<any> {
    return this.currentComponentRef;
  }

  /**
   * Navigates to the next context route.
   * Navigation can be prevented if {@link ContextCanDeactivate} service of the current active route returns false.
   * Before requested route is opened all its {@link ContextResolve} services are called to load initial data.
   *
   * @return promise which is resolved to the boolean value:
   *         <ul>
   *           <li><code>true</code> - only if requested route was opened</li>
   *           <li><code>false</code> - if routing was forbidden by {@link ContextCanDeactivate} service</li>
   *           <li>otherwise throws error</li>
   *         </ul>
   */
  navigate(path: Maybe<string>, params: Hash<any> = {}): Promise<boolean> {
    this.stopCurrentNavigation();

    if (isNil(path)) {
      return Promise.resolve(true);
    }

    const canDeactivate$ = this.canDeactivate().pipe(share$());

    const canDeactivateForbidden$ = canDeactivate$.pipe(filter$((canDeactivate) => !canDeactivate));
    const canDeactivateAllowed$ = canDeactivate$.pipe(filter$(Boolean));

    // start navigation if it is allowed
    const startNavigation$ = canDeactivateAllowed$.pipe(
      // create navigation helper object holding the process of navigation process
      map$(() => new ContextRouterNavigation(
        this.configLoader,
        this.attachment,
        path,
        params)),
      // start navigation process
      tap$((navigation) => {
        navigation.start();
        this.navigation = navigation;
      }),
      // wait till navigation is finished
      switchMap$((navigation) => navigation.promise),
      // when navigation has finished => make navigation or stop navigation process
      tap$({
        next: this.doNavigation.bind(this),
        error: this.doNavigation.bind(this, false),
      }));

    return concat$(canDeactivateForbidden$, startNavigation$).toPromise();
  }

  /**
   * Provided access to the canDeactivate workflow which answers if user can leave current route.
   */
  canDeactivate(): Observable<boolean> {
    const { currentRouteConfig } = this;

    if (isNil(currentRouteConfig)) {
      return of$(true);
    }

    const { route: { canDeactivate }, injector } = currentRouteConfig;
    if (isNil(canDeactivate)) {
      return of$(true);
    }

    const canDeactivateService = injector.get(canDeactivate);
    const canDeactivateAnswer = canDeactivateService.canDeactivate(
      this.currentComponentRef.instance,
      this.currentContextRoute);
    return wrapInObservable<boolean>(canDeactivateAnswer).pipe(first$());
  }

  /**
   * Destroys outlet along with currently displayed component. Stops current route.
   */
  destroy(): void {
    this.destroyComponent();
    this.stopCurrentNavigation();
  }

  /**
   * Allows to attach router outlet to the context lazily. It is necessary, because sometimes outlet can be created
   * even before its context will be available.
   */
  attach(viewContainerRef: ViewContainerRef): void {
    if (this.viewContainerRef) {
      throw new Error('ContextRouterOutlet is already attached');
    }

    this.viewContainerRef = viewContainerRef;

    this.renderComponent();
  }

  /**
   * Answers if this outlet was attached to the any context
   */
  isAttached(): boolean {
    return isNotNil(this.viewContainerRef);
  }

  private stopCurrentNavigation(): void {
    const { navigation } = this;

    if (navigation) {
      navigation.stop();
    }
  }

  private doNavigation(success: boolean): void {
    const { navigation } = this;

    if (isNil(navigation) || !success) {
      return;
    }

    this.destroyComponent();

    if (this.isAttached()) {
      this.renderComponent();
    }
  }

  private destroyComponent(): void {
    const { currentComponentRef } = this;
    if (currentComponentRef) {
      currentComponentRef.destroy();
    }
  }

  private renderComponent(): void {
    const { navigation, viewContainerRef } = this;

    if (isNil(navigation) || isNil(viewContainerRef)) {
      return;
    }

    const { config, contextRoute } = navigation;
    const { injector, route: { component } } = config;

    // we have to get ComponentFactoryResolver using provided Injector,
    // because each module can have own ComponentFactoryResolver
    const resolver = injector.get(ComponentFactoryResolver);
    const componentFactory = resolver.resolveComponentFactory(component);

    // create custom injector to provide ContextRoute into the created component
    const outletInjector = new ContextRouterOutletInjector(contextRoute, viewContainerRef.injector);
    this.currentComponentRef = viewContainerRef.createComponent(
      componentFactory,
      viewContainerRef.length,
      outletInjector);

    this.currentComponentRef.changeDetectorRef.markForCheck();

    this.currentRouteConfig = config;
    this.currentContextRoute = contextRoute;
  }
}

/**
 * ContextRouter allows to abstract the way how content is available to the user (in the context of dialog
 * or in the context of page loaded by @angular/router).
 *
 * ContextRouter has lightweight workflow similar to @angular/router.
 * Developer can easily transform {@link ContextRouter} routes into the dialog routes or @angular/router routes.
 *
 * Each instance of {@link ContextRouter} has own set of associated routes.
 * The main concept of {@link ContextRouter} is a {@link ContextRouterOutlet}.
 * It is the {@link ContextRouterOutlet} that manages context route workflow: navigation between routes,
 * preloading of data using resolve services, calling of canDeactive services.
 * {@link ContextRouter} can have several instantiated instances of {@link ContextRouterOutlet}s each in its own context
 * and having separate workflow.
 */
@Injectable()
export class ContextRouter implements ContextRouteConfigLoader {
  private routes: Hash<InternalContextRouteConfig> = {};

  constructor(private defaultInjector: Injector) {
  }

  createChild(injector: Injector): ContextRouter {
    return new ContextRouter(injector);
  }

  /**
   * Adds new route configuration
   */
  addRoute(route: ContextRouteConfig, injector?: Injector): void {
    this.routes[route.path] = { injector: injector || this.defaultInjector, route };
  }

  /**
   * Loads existing route configuration
   */
  loadRouteConfig(path: string): Observable<InternalContextRouteConfig> {
    return of$(this.getRouteConfig(path));
  }

  /**
   * Retrieves existing route configuration
   */
  getRouteConfig(path: string): InternalContextRouteConfig {
    const config = this.routes[path];

    if (isNil(config)) {
      throw new Error(`ContextRouter: route '${path}' was not defined`);
    }

    return config;
  }

  /**
   * Creates new {@link ContextRouterOutlet}. This outlet will be bind to the given context.
   */
  createOutlet(attachment: ContextRouteAttachment, viewContainerRef?: ViewContainerRef): ContextRouterOutlet {
    return new ContextRouterOutlet(this, attachment, viewContainerRef);
  }
}

/**
 * Injector available to the components created under context route.
 * It provides access to the {@link ContextRoute} instance.
 */
export class ContextRouterOutletInjector implements Injector {
  constructor(private contextRoute: ContextRoute, private parent: Injector) {
  }

  get(token: any, notFoundValue?: any): any {
    if (token === ContextRoute) {
      return this.contextRoute;
    }

    return this.parent.get(token, notFoundValue);
  }
}
